<template>
  <div class="publish-manager">
    <!-- Toast 提示 -->
    <transition name="toast-fade">
      <div v-if="toast.show" :class="['toast', 'toast-' + toast.type]" @click="toast.show = false">
        {{ toast.message }}
      </div>
    </transition>

    <!-- 草稿数据统计 -->
    <section class="pm-section">
      <div class="pm-section-header">
        <h2 class="pm-section-title">草稿数据统计</h2>
        <div class="pm-header-actions">
          <button class="pm-action-btn pm-btn-publish-all" @click="openPublishAllDialog" :disabled="!hasDrafts || isPublishingAll">
            发布所有草稿
          </button>
          <button class="pm-action-btn pm-btn-discard-all" @click="openDiscardAllDialog" :disabled="!hasDrafts || isDiscardingAll">
            放弃所有修改
          </button>
          <button class="pm-action-btn pm-btn-refresh" @click="refreshData">
            刷新
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="pm-loading">加载中...</div>
      <div v-else-if="stats.length === 0" class="pm-empty">暂无数据</div>
      <div v-else class="pm-cards-grid">
        <div
          v-for="stat in stats"
          :key="stat.table"
          class="pm-card"
          :class="{ 'pm-card-active': stat.draftCount > 0 }"
        >
          <!-- 卡片顶部 -->
          <div class="pm-card-header">
            <h4 class="pm-card-title">{{ stat.label }}</h4>
            <span class="pm-card-badge" :class="stat.draftCount > 0 ? 'pm-badge-active' : 'pm-badge-default'">
              {{ stat.draftCount > 0 ? 'Active' : 'No Draft' }}
            </span>
          </div>

          <!-- 数据行 -->
          <div class="pm-card-stats">
            <div class="pm-stat-row">
              <span class="pm-stat-label">当前草稿</span>
              <span class="pm-draft-value" :class="stat.draftCount > 0 ? 'pm-draft-has-value' : 'pm-draft-zero'">
                {{ stat.draftCount > 0 ? stat.draftCount + ' 条草稿' : '无草稿' }}
              </span>
            </div>
            <div class="pm-stat-row">
              <span class="pm-stat-label">已发布数据</span>
              <span class="pm-published-value">{{ stat.publishedCount }} 条</span>
            </div>
          </div>

          <!-- 卡片底部操作区 -->
          <div class="pm-card-footer">
            <button
              class="pm-card-btn pm-card-btn-publish"
              @click="openPublishDialog(stat)"
              :disabled="stat.draftCount === 0 || !stat.hasStatusField || isPublishingTable(stat.table)"
            >
              {{ isPublishingTable(stat.table) ? '发布中...' : '发布板块' }}
            </button>
            <button
              class="pm-card-btn pm-card-btn-discard"
              @click="openDiscardDialog(stat)"
              :disabled="stat.draftCount === 0 || !stat.hasStatusField || isDiscardingTable(stat.table)"
            >
              {{ isDiscardingTable(stat.table) ? '放弃中...' : '放弃修改' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 汇总条 -->
      <div class="pm-summary-bar" v-if="summary">
        <div class="pm-summary-stats">
          <div class="pm-summary-item">
            <div class="pm-summary-dot pm-dot-draft"></div>
            <div>
              <p class="pm-summary-label">草稿总数</p>
              <p class="pm-summary-value pm-value-draft">{{ summary.totalDraft }} <span class="pm-value-unit">条</span></p>
            </div>
          </div>
          <div class="pm-summary-divider"></div>
          <div class="pm-summary-item">
            <div class="pm-summary-dot pm-dot-published"></div>
            <div>
              <p class="pm-summary-label">已发布总数</p>
              <p class="pm-summary-value pm-value-published">{{ summary.totalPublished }} <span class="pm-value-unit">条</span></p>
            </div>
          </div>
        </div>
        <button class="pm-preview-btn" @click="openPreview('HomePage')">管理员预览</button>
      </div>
    </section>

    <!-- 发布历史 -->
    <section class="pm-section">
      <div class="pm-section-header">
        <h2 class="pm-section-title">发布历史</h2>
      </div>
      <div v-if="isHistoryLoading" class="pm-loading">加载中...</div>
      <div v-else-if="history.length === 0" class="pm-empty">暂无发布记录</div>
      <div v-else class="pm-history-table-wrap">
        <table class="pm-history-table">
          <thead>
            <tr>
              <th>操作描述</th>
              <th>执行时间</th>
              <th class="pm-th-right">操作类型</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in history" :key="item.id" class="pm-history-row">
              <td>
                <div class="pm-history-desc">
                  <div class="pm-history-indicator" :class="item.action === 'publish' ? 'pm-indicator-publish' : 'pm-indicator-discard'"></div>
                  <div>
                    <p class="pm-history-main">{{ formatHistoryAction(item) }}</p>
                    <p class="pm-history-sub" v-if="item.notes">{{ item.notes }}</p>
                  </div>
                </div>
              </td>
              <td class="pm-history-time">{{ formatTime(item.published_at) }}</td>
              <td class="pm-th-right">
                <span class="pm-history-badge" :class="item.action === 'publish' ? 'pm-badge-publish' : 'pm-badge-discard'">
                  {{ item.action === 'publish' ? '发布' : '放弃草稿' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- 单表发布确认对话框 -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="showPublishDialog" @click.self="closePublishDialog">
        <div class="modal">
          <h3>确认发布数据</h3>
          <p v-if="activeStat">
            即将发布 <strong>{{ activeStat.label }}</strong> 的草稿数据：
          </p>
          <p class="total">{{ activeStat?.draftCount || 0 }} 条</p>
          <p class="warning-text">发布后，用户将立即看到这些数据。</p>
          <div class="form-group">
            <label>备注（可选）：</label>
            <input
              v-model="publishNotes"
              type="text"
              :placeholder="activeStat ? `如：${activeStat.label}数据更新` : ''"
            />
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" @click="closePublishDialog">取消</button>
            <button
              class="btn btn-confirm"
              @click="confirmPublish"
              :disabled="!activeStat || isPublishingTable(activeStat.table)"
            >
              {{ activeStat && isPublishingTable(activeStat.table) ? '发布中...' : '确认发布' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 单表放弃修改确认对话框 -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="showDiscardDialog" @click.self="closeDiscardDialog">
        <div class="modal danger">
          <h3>确认放弃修改</h3>
          <p v-if="activeStat">
            即将删除 <strong>{{ activeStat.label }}</strong> 的草稿数据：
          </p>
          <p class="total">{{ activeStat?.draftCount || 0 }} 条</p>
          <div class="danger-warning">
            <p>警告：这些数据将被永久删除！</p>
            <p>已发布的数据不会受影响。</p>
          </div>
          <div class="form-group">
            <label>请输入 "确认删除" 以继续：</label>
            <input
              v-model="discardConfirmation"
              type="text"
              placeholder="确认删除"
              @keyup.enter="confirmDiscard"
            />
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" @click="closeDiscardDialog">取消</button>
            <button
              class="btn btn-danger"
              @click="confirmDiscard"
              :disabled="discardConfirmation !== '确认删除' || !activeStat || isDiscardingTable(activeStat.table)"
            >
              {{ activeStat && isDiscardingTable(activeStat.table) ? '删除中...' : '确认删除' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 发布所有草稿对话框 -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="showPublishAllDialog" @click.self="closePublishAllDialog">
        <div class="modal">
          <h3>确认发布所有草稿</h3>
          <p>即将发布以下板块的全部草稿数据：</p>
          <ul class="pm-all-list">
            <li v-for="s in stats.filter(s => s.draftCount > 0 && s.hasStatusField)" :key="s.table">
              <strong>{{ s.label }}</strong> — {{ s.draftCount }} 条
            </li>
          </ul>
          <p class="warning-text">发布后，用户将立即看到所有数据。</p>
          <div class="form-group">
            <label>备注（可选）：</label>
            <input v-model="publishNotes" type="text" placeholder="如：批量数据更新" />
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" @click="closePublishAllDialog">取消</button>
            <button class="btn btn-confirm" @click="confirmPublishAll" :disabled="isPublishingAll">
              {{ isPublishingAll ? '发布中...' : '确认全部发布' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- 放弃所有修改对话框 -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="showDiscardAllDialog" @click.self="closeDiscardAllDialog">
        <div class="modal danger">
          <h3>确认放弃所有修改</h3>
          <p>即将删除以下板块的全部草稿数据：</p>
          <ul class="pm-all-list">
            <li v-for="s in stats.filter(s => s.draftCount > 0 && s.hasStatusField)" :key="s.table">
              <strong>{{ s.label }}</strong> — {{ s.draftCount }} 条
            </li>
          </ul>
          <div class="danger-warning">
            <p>警告：这些数据将被永久删除！</p>
            <p>已发布的数据不会受影响。</p>
          </div>
          <div class="form-group">
            <label>请输入 "确认删除" 以继续：</label>
            <input
              v-model="discardAllConfirmation"
              type="text"
              placeholder="确认删除"
              @keyup.enter="confirmDiscardAll"
            />
          </div>
          <div class="modal-actions">
            <button class="btn btn-secondary" @click="closeDiscardAllDialog">取消</button>
            <button
              class="btn btn-danger"
              @click="confirmDiscardAll"
              :disabled="discardAllConfirmation !== '确认删除' || isDiscardingAll"
            >
              {{ isDiscardingAll ? '删除中...' : '确认全部删除' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { toastMixin } from '@/utils/toastMixin.js';

const TABLE_LABELS = {
  dataasset_listed_companies: '上市公司',
  dataasset_non_listed_companies: '非上市公司',
  dataasset_finance_stock: '股权融资',
  dataasset_finance_other: '其他融资',
  dataasset_finance_abs: 'ABS融资'
};

export default {
  name: 'PublishManager',
  mixins: [toastMixin],
  data() {
    return {
      stats: [],
      summary: null,
      history: [],
      isLoading: false,
      isHistoryLoading: false,
      showPublishDialog: false,
      showDiscardDialog: false,
      showPublishAllDialog: false,
      showDiscardAllDialog: false,
      activeStat: null,
      publishNotes: '',
      discardConfirmation: '',
      discardAllConfirmation: '',
      publishingTable: null,
      discardingTable: null,
      isPublishingAll: false,
      isDiscardingAll: false
    };
  },
  computed: {
    hasDrafts() {
      return this.stats.some(s => s.draftCount > 0 && s.hasStatusField);
    }
  },
  mounted() {
    this.loadStats();
    this.loadHistory();
  },
  methods: {
    refreshData() {
      this.loadStats();
      this.loadHistory();
    },

    openPublishAllDialog() {
      this.publishNotes = '';
      this.showPublishAllDialog = true;
    },

    closePublishAllDialog() {
      this.showPublishAllDialog = false;
      this.publishNotes = '';
    },

    openDiscardAllDialog() {
      this.discardAllConfirmation = '';
      this.showDiscardAllDialog = true;
    },

    closeDiscardAllDialog() {
      this.showDiscardAllDialog = false;
      this.discardAllConfirmation = '';
    },

    async confirmPublishAll() {
      const targets = this.stats.filter(s => s.draftCount > 0 && s.hasStatusField);
      if (!targets.length) return;
      this.isPublishingAll = true;
      try {
        for (const stat of targets) {
          await axios.post(`/api/publish/publish/${stat.table}`, {
            notes: this.publishNotes || `批量发布${stat.label}草稿`
          });
        }
        this.showToast('所有草稿发布成功', 'success');
        this.closePublishAllDialog();
        await this.loadStats();
        await this.loadHistory();
      } catch (err) {
        this.showToast('发布失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.isPublishingAll = false;
      }
    },

    async confirmDiscardAll() {
      if (this.discardAllConfirmation !== '确认删除') {
        this.showToast('请输入正确的确认文字', 'warning');
        return;
      }
      const targets = this.stats.filter(s => s.draftCount > 0 && s.hasStatusField);
      if (!targets.length) return;
      this.isDiscardingAll = true;
      try {
        for (const stat of targets) {
          await axios.post(`/api/publish/discard/${stat.table}`, {
            confirmation: '确认删除'
          });
        }
        this.showToast('所有草稿已放弃', 'success');
        this.closeDiscardAllDialog();
        await this.loadStats();
        await this.loadHistory();
      } catch (err) {
        this.showToast('操作失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.isDiscardingAll = false;
      }
    },

    openPreview(routeName) {
      const routeData = this.$router.resolve({
        name: routeName,
        query: { adminView: 'true' }
      });
      window.open(routeData.href, '_blank');
    },

    isPublishingTable(tableName) {
      return this.publishingTable === tableName;
    },

    isDiscardingTable(tableName) {
      return this.discardingTable === tableName;
    },

    openPublishDialog(stat) {
      this.activeStat = stat;
      this.publishNotes = '';
      this.showPublishDialog = true;
    },

    closePublishDialog() {
      this.showPublishDialog = false;
      this.activeStat = null;
      this.publishNotes = '';
    },

    openDiscardDialog(stat) {
      this.activeStat = stat;
      this.discardConfirmation = '';
      this.showDiscardDialog = true;
    },

    closeDiscardDialog() {
      this.showDiscardDialog = false;
      this.activeStat = null;
      this.discardConfirmation = '';
    },

    async loadStats() {
      this.isLoading = true;
      try {
        const res = await axios.get('/api/publish/draft-stats');
        if (res.data.success) {
          this.stats = res.data.stats;
          this.summary = res.data.summary;
        }
      } catch (err) {
        console.error('加载草稿统计失败:', err);
        this.showToast('加载草稿统计失败', 'error');
      } finally {
        this.isLoading = false;
      }
    },

    async loadHistory() {
      this.isHistoryLoading = true;
      try {
        const res = await axios.get('/api/publish/history');
        if (res.data.success) {
          this.history = res.data.history;
        }
      } catch (err) {
        console.error('加载发布历史失败:', err);
      } finally {
        this.isHistoryLoading = false;
      }
    },

    async confirmPublish() {
      if (!this.activeStat) return;

      this.publishingTable = this.activeStat.table;
      try {
        const res = await axios.post(`/api/publish/publish/${this.activeStat.table}`, {
          notes: this.publishNotes || `发布${this.activeStat.label}草稿`
        });

        if (res.data.success) {
          this.showToast(res.data.message, 'success');
          this.closePublishDialog();
          await this.loadStats();
          await this.loadHistory();
        }
      } catch (err) {
        console.error('发布失败:', err);
        this.showToast('发布失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.publishingTable = null;
      }
    },

    async confirmDiscard() {
      if (!this.activeStat) return;

      if (this.discardConfirmation !== '确认删除') {
        this.showToast('请输入正确的确认文字', 'warning');
        return;
      }

      this.discardingTable = this.activeStat.table;
      try {
        const res = await axios.post(`/api/publish/discard/${this.activeStat.table}`, {
          confirmation: this.discardConfirmation
        });

        if (res.data.success) {
          this.showToast(res.data.message, 'success');
          this.closeDiscardDialog();
          await this.loadStats();
          await this.loadHistory();
        }
      } catch (err) {
        console.error('删除失败:', err);
        this.showToast('删除失败: ' + (err.response?.data?.error || err.message), 'error');
      } finally {
        this.discardingTable = null;
      }
    },

    formatHistoryAction(item) {
      const label = TABLE_LABELS[item.table_name] || item.table_name || '数据';
      const actionText = item.action === 'publish' ? '发布' : '放弃';
      return `${actionText}了 ${label} ${item.record_count} 条数据`;
    },

    formatTime(timeStr) {
      if (!timeStr) return '';
      const date = new Date(timeStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────── */
.publish-manager {
  padding: 4px 4px 32px;
  max-width: 1200px;
}

.pm-section {
  margin-bottom: 36px;
}

/* ── Section header ──────────────────────────────────────── */
.pm-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.pm-section-title {
  font-size: 17px;
  font-weight: 700;
  color: #2e3968;
  border-bottom: none;
  margin: 0;
  padding: 0;
}

/* ── Header action buttons ───────────────────────────────── */
.pm-header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.pm-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.pm-action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.pm-btn-publish-all {
  background: #2e3968;
  color: white;
}
.pm-btn-publish-all:hover:not(:disabled) {
  background: #3d4d87;
}

.pm-btn-discard-all {
  background: #ba1a1a;
  color: white;
}
.pm-btn-discard-all:hover:not(:disabled) {
  background: #d32f2f;
}

.pm-btn-refresh {
  background: #f0f0f0;
  color: #444;
  border: 1px solid #d0d0d0;
}
.pm-btn-refresh:hover {
  background: #e2e2e2;
}

/* ── Cards grid ──────────────────────────────────────────── */
.pm-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

/* ── Individual card ─────────────────────────────────────── */
.pm-card {
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 18px 18px 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s, transform 0.2s;
}

.pm-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.pm-card-active {
  border-color: #2e3968;
  background: #2e3968;
  box-shadow: 0 4px 16px rgba(46, 57, 104, 0.3);
  color: #fff;
}

.pm-card-active:hover {
  background: #3a477e;
  border-color: #3a477e;
  transform: translateY(-2px);
}

.pm-card-active .pm-card-title {
  color: #fff;
}

.pm-card-active .pm-badge-active {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.pm-card-active .pm-stat-label {
  color: rgba(255, 255, 255, 0.65);
}

.pm-card-active .pm-draft-has-value {
  color: #f0c040;
}

.pm-card-active .pm-published-value {
  color: rgba(255, 255, 255, 0.85);
}

.pm-card-active .pm-card-footer {
  border-top-color: rgba(255, 255, 255, 0.15);
}

.pm-card-active .pm-card-btn-publish {
  background: #fff;
  color: #2e3968;
  border-color: #fff;
}
.pm-card-active .pm-card-btn-publish:hover:not(:disabled) {
  background: #f0f4ff;
  border-color: #f0f4ff;
}

.pm-card-active .pm-card-btn-discard {
  background: transparent;
  color: rgba(255, 255, 255, 0.8);
  border-color: rgba(255, 255, 255, 0.35);
}
.pm-card-active .pm-card-btn-discard:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
  color: #fff;
}

/* Card header */
.pm-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.pm-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #2e3968;
  margin: 0;
}

.pm-card-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  white-space: nowrap;
}

.pm-badge-default {
  background: #f0f0f0;
  color: #888;
}

.pm-badge-active {
  background: #c8a84b;
  color: #fff;
}

/* Card stats */
.pm-card-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.pm-stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pm-stat-label {
  font-size: 13px;
  color: #666;
}

.pm-draft-value {
  font-size: 15px;
  font-weight: 700;
}

.pm-draft-has-value {
  color: #c8a84b;
}

.pm-draft-zero {
  color: #ba1a1a;
  font-size: 13px;
}

.pm-published-value {
  font-size: 13px;
  font-weight: 600;
  color: #2e3968;
}

/* Card footer / per-table buttons */
.pm-card-footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding-top: 14px;
  border-top: 1px solid #e8e8e8;
}

.pm-card-btn {
  padding: 8px 6px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.18s;
  border: 1.5px solid transparent;
  letter-spacing: 0.01em;
}

.pm-card-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  filter: grayscale(0.3);
}

.pm-card-btn-publish {
  background: #2e3968;
  color: #fff;
  border-color: #2e3968;
}
.pm-card-btn-publish:hover:not(:disabled) {
  background: #3d4d87;
  border-color: #3d4d87;
  box-shadow: 0 2px 8px rgba(46,57,104,0.25);
}

.pm-card-btn-discard {
  background: #fff5f5;
  color: #c0392b;
  border-color: #f5b7b1;
}
.pm-card-btn-discard:hover:not(:disabled) {
  background: #fdecea;
  border-color: #e74c3c;
  color: #a93226;
  box-shadow: 0 2px 8px rgba(192,57,43,0.15);
}

/* ── Summary bar ─────────────────────────────────────────── */
.pm-summary-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: #eef0f8;
  border: 1px solid #d0d4e8;
  border-radius: 10px;
  flex-wrap: wrap;
  gap: 16px;
}

.pm-summary-stats {
  display: flex;
  align-items: center;
  gap: 28px;
}

.pm-summary-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pm-summary-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pm-dot-draft {
  background: #c8a84b;
  box-shadow: 0 0 0 3px rgba(200,168,75,0.2);
}

.pm-dot-published {
  background: #28a745;
  box-shadow: 0 0 0 3px rgba(40,167,69,0.2);
}

.pm-summary-label {
  font-size: 12px;
  color: #666;
  margin: 0 0 2px;
}

.pm-summary-value {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
}

.pm-value-draft {
  color: #856404;
}

.pm-value-published {
  color: #28a745;
}

.pm-value-unit {
  font-size: 13px;
  font-weight: 400;
  color: #666;
}

.pm-summary-divider {
  width: 1px;
  height: 40px;
  background: #c8cde0;
}

.pm-preview-btn {
  background: #2e3968;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
}
.pm-preview-btn:hover {
  background: #3d4d87;
}

/* ── History table ───────────────────────────────────────── */
.pm-history-table-wrap {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
}

.pm-history-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.pm-history-table thead tr {
  background: #f5f3f7;
}

.pm-history-table th {
  padding: 12px 20px;
  font-weight: 700;
  color: #555;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  white-space: nowrap;
}

.pm-th-right {
  text-align: right !important;
}

.pm-history-row {
  transition: background 0.15s;
}
.pm-history-row:hover {
  background: #f9f8fb;
}

.pm-history-table td {
  padding: 14px 20px;
  border-bottom: 1px solid #f0edf2;
  vertical-align: middle;
}

.pm-history-table tbody tr:last-child td {
  border-bottom: none;
}

.pm-history-desc {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pm-history-indicator {
  width: 4px;
  height: 36px;
  border-radius: 3px;
  flex-shrink: 0;
}

.pm-indicator-publish {
  background: #28a745;
}

.pm-indicator-discard {
  background: #ba1a1a;
}

.pm-history-main {
  font-weight: 600;
  color: #2e3968;
  margin: 0 0 2px;
}

.pm-history-sub {
  font-size: 12px;
  color: #888;
  font-style: italic;
  margin: 0;
}

.pm-history-time {
  color: #666;
  white-space: nowrap;
  font-size: 12px;
}

.pm-history-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.pm-badge-publish {
  background: #dde1ff;
  color: #2e3968;
}

.pm-badge-discard {
  background: #ffdad6;
  color: #93000a;
}

/* ── "All" dialog list ───────────────────────────────────── */
.pm-all-list {
  margin: 10px 0 14px 4px;
  padding-left: 18px;
  font-size: 14px;
  color: #444;
  line-height: 1.9;
}

/* ── Utility ─────────────────────────────────────────────── */
.pm-loading, .pm-empty {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* ── Modal ───────────────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.48);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 12px;
  padding: 28px 28px 24px;
  max-width: 480px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}

.modal h3 {
  margin: 0 0 14px;
  font-size: 17px;
  font-weight: 700;
  color: #2e3968;
}

.modal .total {
  font-weight: 700;
  font-size: 20px;
  margin: 10px 0;
  color: #2e3968;
}

.modal .warning-text {
  color: #666;
  font-size: 13px;
  margin: 6px 0;
}

.modal.danger h3 {
  color: #ba1a1a;
}

.danger-warning {
  background: #ffdad6;
  border: 1px solid #f5c6cb;
  padding: 10px 14px;
  border-radius: 6px;
  margin: 14px 0;
}

.danger-warning p {
  margin: 3px 0;
  color: #93000a;
  font-size: 13px;
}

.form-group {
  margin: 14px 0;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #2e3968;
  font-size: 13px;
}

.form-group input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font-size: 14px;
  color: #2e3968;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus {
  outline: none;
  border-color: #2e3968;
  box-shadow: 0 0 0 3px rgba(46, 57, 104, 0.1);
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

/* ── Shared modal buttons ────────────────────────────────── */
.btn {
  padding: 8px 18px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f0f0f0;
  color: #333;
  border-color: #ccc;
}
.btn-secondary:hover:not(:disabled) {
  background: #e0e0e0;
}

.btn-confirm {
  background: #2e3968;
  color: white;
}
.btn-confirm:hover:not(:disabled) {
  background: #3d4d87;
}

.btn-danger {
  background: #ba1a1a;
  color: white;
}
.btn-danger:hover:not(:disabled) {
  background: #d32f2f;
}

/* ── Toast ───────────────────────────────────────────────── */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  z-index: 9999;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 500px;
}
.toast-success { background: #d4edda; color: #155724; border: 1px solid #c3e6cb; }
.toast-error   { background: #ffdad6; color: #93000a; border: 1px solid #f5c6cb; }
.toast-warning { background: #fff3cd; color: #856404; border: 1px solid #ffeeba; }

.toast-fade-enter-active, .toast-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.toast-fade-enter, .toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}

.modal-fade-enter-active { transition: opacity 0.25s ease; }
.modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-fade-enter-active .modal { animation: modal-scale-in 0.25s ease; }
.modal-fade-leave-active .modal  { animation: modal-scale-out 0.2s ease; }

@keyframes modal-scale-in {
  from { opacity: 0; transform: scale(0.95) translateY(-10px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
}
@keyframes modal-scale-out {
  from { opacity: 1; transform: scale(1)    translateY(0); }
  to   { opacity: 0; transform: scale(0.95) translateY(10px); }
}
</style>
